import type { ReactNode } from "react";
import { Clock, ShoppingBag } from "lucide-react";
import { cn } from "../lib/utils";
import {
  serviceItemImageObjectPositionClass,
  type ServiceItemImageObjectPosition,
} from "../lib/service-item-image-object-position";
import { ImageWithSkeleton } from "./image-skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./select";
import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip";

/** Valeur Radix : RDV sans entrée catalogue (ex. modal praticien). */
export const SESSION_TYPE_SERVICE_SELECT_NONE = "__session_type_none__";

/** Valeur Radix : aucune prestation choisie encore (ex. portail client). */
export const SESSION_TYPE_SERVICE_SELECT_UNSELECTED =
  "__session_unselected__";

export type SessionTypeServiceSelectItem = {
  id: string;
  label: string;
  descriptionPlain: string | null;
  durationMin: number;
  /** Prix déjà formaté côté app (devise, locale). */
  priceLabel: string;
  imageUrl: string | null;
  imageObjectPosition?: ServiceItemImageObjectPosition;
  disabled?: boolean;
  dimmed?: boolean;
  remainingSlot?: ReactNode;
};

export type SessionTypeServiceSelectRowProps = {
  label: string;
  descriptionPlain: string | null;
  durationMin: number;
  priceLabel: string;
  imageUrl: string | null;
  imageObjectPosition?: ServiceItemImageObjectPosition;
  dimmed?: boolean;
  remainingSlot?: ReactNode;
};

export const SessionTypeServiceSelectRow = ({
  label,
  descriptionPlain,
  durationMin,
  priceLabel,
  imageUrl,
  imageObjectPosition,
  dimmed,
  remainingSlot,
}: SessionTypeServiceSelectRowProps) => {
  const desc = descriptionPlain?.trim() || null;

  return (
    <div
      className={cn(
        "flex w-full max-w-full items-start gap-3 py-0.5",
        dimmed && "opacity-60",
      )}
    >
      <div className="border-border/60 bg-muted/25 relative h-12 w-12 shrink-0 overflow-hidden rounded-lg border shadow-sm">
        {imageUrl ? (
          <ImageWithSkeleton
            src={imageUrl}
            alt=""
            className="absolute inset-0 h-full w-full"
            imageClassName={cn(
              "h-full w-full object-cover",
              serviceItemImageObjectPositionClass(imageObjectPosition),
            )}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <ShoppingBag className="text-primary/45 h-5 w-5" />
          </div>
        )}
      </div>
      <div className="flex min-h-[3rem] min-w-0 flex-1 flex-col justify-start gap-0.5 overflow-hidden">
        <div className="text-foreground text-sm leading-snug font-semibold wrap-break-word">
          {label}
        </div>
        {desc ? (
          <Tooltip delayDuration={400}>
            <TooltipTrigger asChild>
              <span className="text-muted-foreground line-clamp-2 w-full cursor-default text-left text-xs leading-snug wrap-break-word">
                {desc}
              </span>
            </TooltipTrigger>
            <TooltipContent
              side="left"
              align="start"
              className="z-[130] max-w-sm text-left text-sm whitespace-pre-wrap"
            >
              {desc}
            </TooltipContent>
          </Tooltip>
        ) : null}
      </div>
      <div className="text-muted-foreground flex shrink-0 flex-col items-end justify-start gap-1 self-stretch text-xs">
        <span className="flex items-center gap-1 whitespace-nowrap tabular-nums">
          <Clock className="h-3.5 w-3.5 shrink-0 opacity-80" />
          {durationMin} min
        </span>
        <span className="text-foreground text-sm font-semibold tabular-nums whitespace-nowrap">
          {priceLabel}
        </span>
        {remainingSlot ? (
          <span className="max-w-[8rem] text-end leading-tight">{remainingSlot}</span>
        ) : null}
      </div>
    </div>
  );
};

export type SessionTypeServiceSelectProps = {
  value: string | null;
  onValueChange: (serviceItemId: string | null) => void;
  items: SessionTypeServiceSelectItem[];
  disabled?: boolean;
  ariaLabel: string;
  /** Modal praticien : option « sans catalogue » → `null`. */
  catalogFreeOption?: { title: string; subtitle: string };
  /** Portail client : première ligne « pas encore choisi » → `null`. */
  unselectedOptionLabel?: string;
  selectPlaceholder?: string;
  contentClassName?: string;
  triggerClassName?: string;
};

export const SessionTypeServiceSelect = ({
  value,
  onValueChange,
  items,
  disabled,
  ariaLabel,
  catalogFreeOption,
  unselectedOptionLabel,
  selectPlaceholder,
  contentClassName,
  triggerClassName,
}: SessionTypeServiceSelectProps) => {
  const itemClassName =
    "cursor-pointer items-start rounded-lg border border-transparent px-2 py-2 pr-8 transition-colors data-[state=checked]:border-primary/25 data-[state=checked]:bg-primary/10 data-[state=checked]:text-foreground data-[highlighted]:bg-muted data-[highlighted]:text-foreground";

  const hasFree = Boolean(catalogFreeOption);
  const hasUnselected = Boolean(unselectedOptionLabel);

  let internalValue: string;
  if (value !== null) {
    internalValue = value;
  } else if (hasFree) {
    internalValue = SESSION_TYPE_SERVICE_SELECT_NONE;
  } else {
    internalValue = SESSION_TYPE_SERVICE_SELECT_UNSELECTED;
  }

  const handleChange = (v: string) => {
    if (
      v === SESSION_TYPE_SERVICE_SELECT_NONE ||
      v === SESSION_TYPE_SERVICE_SELECT_UNSELECTED
    ) {
      onValueChange(null);
      return;
    }
    onValueChange(v);
  };

  const selectedItem =
    value !== null ? items.find((i) => i.id === value) : undefined;

  return (
    <Select value={internalValue} onValueChange={handleChange} disabled={disabled}>
      <SelectTrigger
        aria-label={ariaLabel}
        className={cn(
          "h-auto min-h-11 gap-2.5 py-2.5 text-left [&>span]:line-clamp-none",
          triggerClassName,
        )}
      >
        {selectedItem ? (
          <div className="border-border/60 bg-muted/25 relative mt-0.5 h-9 w-9 shrink-0 overflow-hidden rounded-md border shadow-sm">
            {selectedItem.imageUrl ? (
              <ImageWithSkeleton
                src={selectedItem.imageUrl}
                alt=""
                className="absolute inset-0 h-full w-full"
                imageClassName={cn(
                  "h-full w-full object-cover",
                  serviceItemImageObjectPositionClass(
                    selectedItem.imageObjectPosition,
                  ),
                )}
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center">
                <ShoppingBag className="text-primary/45 h-4 w-4" />
              </div>
            )}
          </div>
        ) : null}
        <SelectValue
          placeholder={selectPlaceholder}
          className="min-w-0 flex-1 text-left leading-snug"
        />
      </SelectTrigger>
      <SelectContent
        position="popper"
        sideOffset={4}
        className={cn(
          "max-h-[min(22rem,55vh)] w-[var(--radix-select-trigger-width)]",
          contentClassName,
        )}
      >
        {hasFree && catalogFreeOption ? (
          <SelectItem
            value={SESSION_TYPE_SERVICE_SELECT_NONE}
            textValue={catalogFreeOption.title}
            className={itemClassName}
          >
            <div className="flex w-full flex-col gap-0.5 pr-1 text-left">
              <span className="text-sm font-medium">
                {catalogFreeOption.title}
              </span>
              <span className="text-muted-foreground text-xs leading-snug">
                {catalogFreeOption.subtitle}
              </span>
            </div>
          </SelectItem>
        ) : null}
        {!hasFree && hasUnselected ? (
          <SelectItem
            value={SESSION_TYPE_SERVICE_SELECT_UNSELECTED}
            textValue={unselectedOptionLabel}
            className={itemClassName}
          >
            <span className="text-muted-foreground pr-1 text-sm">
              {unselectedOptionLabel}
            </span>
          </SelectItem>
        ) : null}
        {items.map((item) => (
          <SelectItem
            key={item.id}
            value={item.id}
            textValue={item.label}
            disabled={item.disabled}
            className={cn(
              itemClassName,
              item.disabled && "cursor-not-allowed",
            )}
          >
            <SessionTypeServiceSelectRow
              label={item.label}
              descriptionPlain={item.descriptionPlain}
              durationMin={item.durationMin}
              priceLabel={item.priceLabel}
              imageUrl={item.imageUrl}
              imageObjectPosition={item.imageObjectPosition}
              dimmed={item.dimmed}
              remainingSlot={item.remainingSlot}
            />
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};
